/**
 * The toast with Undo (the prototype's `flash(text, canUndo)`): `#toast` slides in for 5 s; Undo calls
 * `engine.undo(undoId)` for the mutation that produced it.
 */

import { useEffect, useState } from 'react';
import { toDigesterError } from '@digester/core';
import { useEngine } from '../engine-client/EngineProvider.js';
import { ui, useUi } from './ui-store.js';

const VISIBLE_MS = 5000;
let nextToast = 1;

/**
 * Show a message; pass the mutation's `undoId` to offer Undo — or several, in the order they were made,
 * when one action wrote several undoable changes (Topics dialog: topics, then tags). Undo reverses them.
 */
export function showToast(text: string, undo: string | readonly (string | null)[] | null = null): void {
  const undoIds = (typeof undo === 'string' ? [undo] : (undo ?? [])).filter((id): id is string => typeof id === 'string' && id !== '');
  ui.set({ toast: { id: nextToast++, text, undoIds } });
}

/** Undo every change of a toast, newest first. */
export async function undoAll(engine: { undo(id: string): Promise<unknown> }, undoIds: readonly string[]): Promise<void> {
  for (const id of [...undoIds].reverse()) await engine.undo(id);
}

export function Toast() {
  const toast = useUi((s) => s.toast);
  const engine = useEngine();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!toast) return;
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [toast]);

  const undo = async (undoIds: readonly string[]): Promise<void> => {
    setVisible(false);
    try {
      await undoAll(engine, undoIds);
      showToast('Undone');
    } catch (error) {
      showToast(toDigesterError(error).message);
    }
  };

  return (
    <div id="toast" role="status" aria-live="polite" className={visible ? 'visible' : undefined}>
      {toast ? <span>{toast.text}</span> : null}
      {toast && toast.undoIds.length > 0 ? (
        <button type="button" onClick={() => void undo(toast.undoIds)}>
          Undo
        </button>
      ) : null}
    </div>
  );
}
